import React, { useRef } from "react"
import { useClickOutside } from "../../hooks"
import { motion, AnimatePresence } from "framer-motion"
import BusinessManImg from "../../../../../assets/avia/search/businessman.png"
import ChildrenImg from "../../../../../assets/avia/search/children.png"
import BabyImg from "../../../../../assets/avia/search/babis.png"
import { useDispatch, useSelector } from "react-redux"
import {
  selectAviaSearchPassengers,
  setAviaSearchParams,
} from "../../../../../app/reducers/book/aviaSearchSlice"
import { useTranslation, Trans } from "react-i18next"
import { Plus, Minus } from "lucide-react"

const DEFAULT_MAX_PASSENGERS = {
  adults: 6,
  children: 3,
  infants: 11,
}

const PassengerModal = ({ showModal, onClose, maxPassengers }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const { children, adults, infants } = useSelector(selectAviaSearchPassengers)

  const limits = maxPassengers || DEFAULT_MAX_PASSENGERS

  const modalRef = useRef(null)
  useClickOutside(modalRef, onClose)

  const updateCount = (category, type, delta) => {
    if (category !== "passengers") return

    const currentValue = type === "adults" ? adults : type === "children" ? children : infants

    const newValue = Math.max(0, currentValue + delta)

    // жёсткий лимит по конкретной категории
    if (delta > 0 && newValue > limits[type]) {
      return
    }

    const total = adults + children + infants - currentValue + newValue

    if (total === 0) {
      return
    }

    dispatch(
      setAviaSearchParams({
        key: `passengers.${type}`,
        value: newValue,
      })
    )
  }

  return (
    <AnimatePresence>
      {showModal && (
        <motion.div
          ref={modalRef}
          initial={{ opacity: 0, scale: 0.95, y: -10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -10 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          className="absolute right-0 top-[60px] w-max min-w-[280px] bg-white rounded-lg shadow-lg z-10"
        >
          <div>
            <CounterRow
              icon={BusinessManImg}
              label={t("admin.adult")}
              subtitle={<Trans i18nKey="admin.over_years" values={{ years: 14 }} />}
              value={adults}
              onIncrement={() => updateCount("passengers", "adults", 1)}
              onDecrement={() => updateCount("passengers", "adults", -1)}
              disabledIncrement={adults >= limits.adults}
            />
            <CounterRow
              icon={ChildrenImg}
              label={t("admin.child")}
              subtitle={<Trans i18nKey="admin.from_to_years" values={{ first: 2, second: 14 }} />}
              value={children}
              onIncrement={() => updateCount("passengers", "children", 1)}
              onDecrement={() => updateCount("passengers", "children", -1)}
              disabledIncrement={children >= limits.children}
            />

            <CounterRow
              icon={BabyImg}
              label={t("admin.infant")}
              subtitle={<Trans i18nKey="admin.from_to_years" values={{ first: 0, second: 2 }} />}
              value={infants}
              onIncrement={() => updateCount("passengers", "infants", 1)}
              onDecrement={() => updateCount("passengers", "infants", -1)}
              disabledIncrement={infants >= limits.infants}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default PassengerModal

const CounterRow = ({
  icon,
  label,
  subtitle,
  value,
  onIncrement,
  onDecrement,
  disabledIncrement,
}) => (
  <div className="flex items-center justify-between py-1 px-2">
    <div className="flex items-center gap-3">
      <img src={icon} alt="" className="w-5 h-5" />

      <div>
        <div className="text-dark-gray text-sm font-medium">{label}</div>
        {subtitle && <div className="text-xs text-[10px] text-light-gray">{subtitle}</div>}
      </div>
    </div>

    <div className="flex items-center gap-2">
      <button
        onClick={onDecrement}
        className="w-5 h-5 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
        disabled={value === 0}
      >
        <Minus className="h-3 w-3 text-black" />
      </button>

      <span className="w-6 text-center text-[15px] font-medium text-text-main">{value}</span>

      <button
        onClick={onIncrement}
        className="w-5 h-5 rounded-full bg-second-success/30 flex items-center justify-center hover:bg-success transition-colors cursor-pointer font-sans disabled:opacity-30 disabled:cursor-not-allowed"
        disabled={disabledIncrement}
      >
        <Plus className="h-3 w-3 text-black" />
      </button>
    </div>
  </div>
)
